// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## Interval ID
//
import type { ClefName } from 'shared/definitions/musicTheory/clefs';
import { isClefName } from 'shared/definitions/musicTheory/clefs';
import type { NoteName } from 'shared/definitions/musicTheory/notesAndAccidentals';
import type { IntervalName } from 'shared/definitions/musicTheory/intervals';
import type { AnswerAndData } from 'shared/definitions/answer-and-data';
import InteractivePrototype from './interactivePrototype';
import type { InteractiveOptions } from './interactivePrototype';

import QuestionGenerator from '../uTheory.Skills/questionGenerator';
import SkillCalculator from '../uTheory.Skills/skillCalculator';
import * as Skills from '../uTheory.Skills/skills';
import Note from '../uTheory.Notation/note';
import Interval from '../uTheory.Notation/interval';
import Clef from '../uTheory.Notation/clef';
import type IntervalQuestion from '../uTheory.Skills/intervalQuestion';
import NoteHolder from '../uTheory.Notation/noteHolder';
import Measure from '../uTheory.Notation/measure';
import Voice from '../uTheory.Notation/voice';
import KeySignature from '../uTheory.Notation/keySignature';
import Staff from '../uTheory.Notation/staff';
import Music from '../uTheory.Notation/music';
import type {
  LetterPanelButtonOrBRObject,
  LetterPanelOptions,
} from '../uTheory.Notation/letterPanel';

interface IntervalsIDOptions extends InteractiveOptions {
  lowestNote?: Note | string;
  highestNote?: Note | string;
  customButtons?: {
    text: string;
    value: string;
    hotKey: string | string[];
  }[];
  /** Only show buttons for half & whole steps */
  halfWholeOnly?: boolean;
  /** If in halfWholeOnly mode, say "Chromatic Half Step" instead of CHS, etc... */
  longHalfWholeNames?: boolean;
  grandStaff?: boolean;
  questions?: string[];
  decoyInterval?: Interval | string;
}

export interface IntervalsIDQuestion {
  question: {
    note: Note;
    note2: Note;
    interval: Interval;
    clef: ClefName;
  };
  answer: {
    intervalName: string;
    intervalQuality: string;
  };
  text: string;
  skills: (string | number)[];
}

// Interval name type options:
//    longNames: Perfect 4th
//    genericNames: 4th
//      The default is: (intervalname) + above/below.
//    directionOnly: if you want to specify interval name & drill a specific interval only.

class IntervalsID extends InteractivePrototype {
  skillCalculator: SkillCalculator = new SkillCalculator();

  declare options: IntervalsIDOptions;

  answer!: {
    intervalName: string;
    intervalQuality: string;
  };

  declare currentQuestion: IntervalsIDQuestion;

  declare questions: IntervalsIDQuestion[];

  private note1Holder!: NoteHolder;

  private note2Holder!: NoteHolder;

  /* only present if options.gradStaff === true */
  private note1HolderBass?: NoteHolder;

  /* only present if options.gradStaff === true */
  private note2HolderBass?: NoteHolder;

  /* only present if options.gradStaff === true */
  private noteHolders?: NoteHolder[];

  private clef!: Clef;

  constructor(displayDiv: HTMLDivElement, options: IntervalsIDOptions = {}) {
    super(displayDiv, options);
    this.targetSpeedFromSkills = IntervalsID.registry
      .map((r) => r.skills)
      .reduce((a, b) => a.concat(b));

    this.settings = {
      instructions: {
        ariaLive: 'off',
      },
      notation: {
        fixedWidth: 180,
        ariaTitle: () => {
          if (!this.currentQuestion) return '';
          const note1String = this.currentQuestion.question.note.toAriaString();
          const note2String = this.currentQuestion.question.note2.toAriaString();
          return `${note1String} ${note2String}`;
        },
        ariaLive: 'alert',
      },
      letters: {
        numberButtons: true,
        intervalQualityButtons: true,
        checkButton: true,
      },
      checkMark: {
        rightAnswerLength: 0.6,
        rightAnswerInOutRatio: 0.7,
        wrongAnswerLength: 1.0,
        wrongAnswerInOutRatio: 0.3,
      },
      player: {},
      oneTryOnly: false,
      progressTracker: true,
    };
    this.init(displayDiv, options);
  }

  init(displayDiv: HTMLDivElement, options: IntervalsIDOptions): Promise<any> {
    this.answer = {
      intervalName: '',
      intervalQuality: '',
    };

    if (options.halfWholeOnly === true) {
      console.log('using half whole only buttons');
      const letters = this.settings.letters as Partial<LetterPanelOptions>;

      letters.numberButtons = false;
      letters.intervalQualityButtons = false;
      letters.checkButton = false;

      letters.customButtons = options.customButtons || [
        {
          text: options.longHalfWholeNames ? 'Chromatic Half Step' : 'CHS',
          value: 'A1',
          hotKey: ['c', 'C'],
        },
        ...(options.longHalfWholeNames ? [{ tagName: 'br' } as LetterPanelButtonOrBRObject] : []),
        {
          text: options.longHalfWholeNames ? 'Diatonic Half Step' : 'DHS',
          value: 'm2',
          hotKey: ['d', 'D'],
        },
        ...(options.longHalfWholeNames ? [{ tagName: 'br' } as LetterPanelButtonOrBRObject] : []),
        { text: options.longHalfWholeNames ? 'Whole Step' : 'WS', value: 'M2', hotKey: ['w', 'W'] },
        { text: 'Other', value: 'Other', hotKey: ['o', 'O'] },
      ];
      options.skills = options.skills || ['chs', 'dhs', 'ws'];
    }

    if (options.lowestNote === undefined) {
      options.lowestNote = 'C2';
    }

    if (options.highestNote === undefined) {
      options.highestNote = 'C6';
    }

    // GCR TODO: If not, initialize others.

    // now call the prototype's init function:

    return super.init(displayDiv, options);
  }

  generateQuestions(): void {
    const options = this.options;
    this.questions = this.getQuestions(options);
  }

  getQuestions(options: IntervalsIDOptions): IntervalsIDQuestion[] {
    const drill = options.drill !== undefined && options.drill === true;
    return drill === true
      ? this.generateDrillQuestions(options)
      : this.prepareManualQuestions(options);
  }

  processQuestion(questionString: string, index: number): IntervalsIDQuestion {
    const questionParts = questionString.split('/');
    const note = new Note(questionParts[0] as NoteName);
    const interval = new Interval(questionParts[1] as IntervalName);
    const note2 = note.getNoteAt(interval);
    if (!note2) {
      throw new Error(`${note} ${interval} is not a possible interval!`);
    }
    const skills = this.calculateSkill(interval);
    const clef = isClefName(questionParts[2])
      ? questionParts[2]
      : IntervalsID.getClefByNotes(note, note2);

    return {
      question: {
        note,
        note2,
        interval,
        clef,
      },
      answer: {
        intervalName: `${interval.genericInterval}`,
        intervalQuality: interval.quality,
      },
      text: `${index + 1}`,
      skills,
    };
  }

  prepareManualQuestions(options: IntervalsIDOptions): IntervalsIDQuestion[] {
    return (options.questions || []).map(
      (question: string, index: number): IntervalsIDQuestion =>
        this.processQuestion(question, index)
    );
  }

  static getClefByNotes(note: Note, note2: Note): ClefName {
    if (note.isGreaterThan('F4') || note2.isGreaterThan('F4')) {
      return 'treble';
    }
    if (note.isLessThan('G3') || note2.isLessThan('G3')) {
      return 'bass';
    }
    return Math.random() > 0.5 ? 'treble' : 'bass';
  }

  generateDrillQuestions(options: IntervalsIDOptions): IntervalsIDQuestion[] {
    const questions: IntervalsIDQuestion[] = [];
    const generator = new QuestionGenerator();
    if (!options.skills) {
      throw new Error("Can't generate drill questions without options.skills.");
    }
    const optionsSkills = options.skills;
    const maxQuestions = options.maxQuestions || 8;
    const numQuestionsPerSkill = Math.ceil(maxQuestions / optionsSkills.length) + 1;
    const decoyInterval = options.decoyInterval;
    let generatedQuestions: IntervalQuestion[] = [];
    let skillName;

    optionsSkills.forEach((skill: string | number): void => {
      skillName = Skills.makeString(skill);
      generatedQuestions = generatedQuestions.concat(
        (generator[skillName as keyof typeof generator] as (...arg0: any) => IntervalQuestion[])(
          numQuestionsPerSkill,
          options
        )
      );
    });

    if (options.halfWholeOnly === true && options.decoyInterval !== undefined) {
      const decoys = generator.interval_helper(
        Math.floor(maxQuestions / 3),
        options,
        decoyInterval
      );
      generatedQuestions = generatedQuestions.concat(decoys);
    }

    generatedQuestions = generator.rejectDuplicates(generatedQuestions, maxQuestions);

    generatedQuestions.forEach((question: IntervalQuestion, index: number): void => {
      const note = question.note;
      const interval = question.interval;
      const note2 = note.getNoteAt(interval) as Note;
      const skills = this.calculateSkill(interval);
      let clef: ClefName;

      if (note.isGreaterThan('F4') || note2.isGreaterThan('F4')) {
        clef = 'treble';
      } else if (note.isLessThan('G3') || note2.isLessThan('G3')) {
        clef = 'bass';
      } else {
        clef = Math.random() > 0.5 ? 'treble' : 'bass';
      }

      questions.push({
        question: {
          note,
          note2,
          interval,
          clef,
        },
        answer: {
          intervalName: `${interval.genericInterval}`,
          intervalQuality: interval.quality,
        },
        text: `${index + 1}`,
        skills,
      });
    }, this);

    return questions;
  }

  calculateSkill(interval: Interval): (number | string)[] {
    if (this.optionsTrue('halfWholeOnly')) {
      const skill = this.skillCalculator.getStaffHalfWholeSkill(interval);
      return skill ? [skill] : [];
    }
    return this.skillCalculator.getStaffIntervalSkills(interval);
  }

  initializeMusic(): void {
    const clef = new Clef('treble');
    const staff = new Staff(clef);

    this.clef = clef;
    this.note1Holder = new NoteHolder(new Note(), 'h');
    this.note2Holder = new NoteHolder(new Note(), 'h');

    const measure = new Measure(this.note1Holder, this.note2Holder);
    const voice = new Voice(measure);
    voice.setStaff(staff);
    const keySig = new KeySignature('C', 'Major');

    if (this.options.grandStaff) {
      const clef2 = new Clef('bass');
      const staff2 = new Staff(clef2);
      const note1HolderBass = new NoteHolder('', 'h');
      const note2HolderBass = new NoteHolder('', 'h');
      const measureBass = new Measure(note1HolderBass, note2HolderBass);
      const voiceBass = new Voice(measureBass);
      voiceBass.setStaff(staff2);
      this.note1HolderBass = note1HolderBass;
      this.note2HolderBass = note2HolderBass;
      this.music = new Music(staff, voice, staff2, voiceBass, keySig);

      this.noteHolders = [
        this.note1Holder,
        this.note2Holder,
        this.note1HolderBass,
        this.note2HolderBass,
      ];
    } else {
      this.music = new Music(staff, voice, keySig);
    }
  }

  newQuestion(): void {
    this.questionPrompt = this.getQuestionPrompt();
    this.progressTracker!.start(this.targetSpeed);

    const question = this.questions[this.questionNumber]!.question;
    const note = question.note;
    const note2 = question.note2;
    const clef = question.clef;

    if (!this.optionsTrue('halfWholeOnly')) {
      if (question.interval.genericInterval > 8) {
        this.letterPanel!.switchNumbersButtons(2);
      } else {
        this.letterPanel!.switchNumbersButtons(1);
      }
    }
    super.newQuestion();

    if (this.options.grandStaff) {
      this.noteHolders!.forEach((nh) => {
        nh.length = 0;
      });
      const note1Prefers = note.prefersClef();
      const note1Clef = note1Prefers[Math.floor(Math.random() * note1Prefers.length)];
      const note2Prefers = note2.prefersClef();
      const note2Clef = note2Prefers[Math.floor(Math.random() * note2Prefers.length)];

      if (note1Clef === 'treble') {
        this.noteHolders![0]!.push(note);
      } else {
        this.noteHolders![2]!.push(note);
      }

      if (note2Clef === 'treble') {
        this.noteHolders![1]!.push(note2);
      } else {
        this.noteHolders![3]!.push(note2);
      }
    } else {
      this.note1Holder.notes[0] = note;
      this.note2Holder.notes[0] = note2;
      this.clef.setName(clef);
    }
    this.notation!.render();

    this.wrongAnswerCount = 0;
  }

  getQuestionPrompt(): string {
    const testMode = this.optionsTrue('testMode');
    let questionPrompt;

    questionPrompt =
      this.options.questionPrompt ||
      "Name the interval: <span id='intervalsIdAnswerSpan' aria-live='polite'>____</span>";

    if (testMode === false) {
      questionPrompt += `<br />#{question} of ${this.questions.length}`;
    }

    return questionPrompt;
  }

  newCheckpointQuestion(questionString: string): Promise<IntervalsIDQuestion> {
    return this.loadingPromise.then(() => {
      const questionParts = questionString.split('/');
      const note = new Note(questionParts[0] as NoteName);
      const interval = new Interval(questionParts[1] as IntervalName);
      const note2 = note.getNoteAt(interval) as Note;
      const skills = this.calculateSkill(interval);
      const clef = IntervalsID.getClefByNotes(note, note2);
      const questions = [];

      questions.push({
        question: {
          note,
          note2,
          interval,
          clef,
        },
        answer: {
          intervalName: `${interval.genericInterval}`,
          intervalQuality: interval.quality,
        },
        skills,
        text: 'No text implemented.',
      });

      this.questions = questions;

      this.newQuestion();
      this.resize();
      return Promise.resolve(this.currentQuestion);
    });
  }

  addListeners(): void {
    if (this.options.halfWholeOnly === true) {
      this.letterPanel!.addCustomButtonCallback(this.checkHalfWholeAnswer, this);
    } else {
      this.letterPanel!.addNumberCallback(this.addIntervalNameToAnswer, this);
      this.letterPanel!.addIntervalQualityCallback(this.addIntervalQualityToAnswer, this);
      this.letterPanel!.addCheckCallback(this.checkAnswer, this);
    }
  }

  addIntervalNameToAnswer(generic: string | number): void {
    if (typeof generic === 'number') {
      generic = `${generic}`;
    }
    this.answer.intervalName = generic;

    return this.displayAnswer();
  }

  addIntervalQualityToAnswer(quality: string | number): void {
    if (typeof quality === 'number') {
      quality = `${quality}`;
    }
    this.answer.intervalQuality = quality;

    return this.displayAnswer();
  }

  displayAnswer(): void {
    const answerDiv = document.getElementById('intervalsIdAnswerSpan');
    const answer = this.answer.intervalQuality + this.answer.intervalName;
    if (!answerDiv) return;
    if (answer === '') {
      answerDiv.setAttribute('aria-live', 'off');
      answerDiv.innerHTML = '__';
    } else {
      answerDiv.setAttribute('aria-live', 'polite');
      answerDiv.innerHTML = answer;
    }
  }

  checkHalfWholeAnswer(answerString: 'm2' | 'M2' | 'A1' | 'Other' | string | number): void {
    if (this.rightAnswerDelay) return;
    let answer: 'm2' | 'M2' | 'A1' | 'Other';
    if (typeof answerString === 'string' && ['m2', 'M2', 'A1', 'Other'].includes(answerString)) {
      answer = answerString as typeof answer;
    } else {
      answer = 'Other';
    }

    const testMode = this.optionsTrue('testMode');
    const question = this.questions[this.questionNumber]!.question;
    const interval = `${question.interval.quality}${question.interval.genericInterval}`;
    switch (answer) {
      case 'm2':
        this.answer = {
          intervalName: 'DHS',
          intervalQuality: '',
        };
        break;
      case 'M2':
        this.answer = {
          intervalName: 'WS',
          intervalQuality: '',
        };
        break;
      case 'A1':
        this.answer = {
          intervalName: 'CHS',
          intervalQuality: '',
        };
        break;
      case 'Other':
        this.answer = {
          intervalName: 'Other',
          intervalQuality: '',
        };
        break;
    }
    const correct =
      answer === interval ||
      (answer === 'Other' && interval !== 'm2' && interval !== 'M2' && interval !== 'A1');

    if (testMode === true) {
      this.checkpointAnswer(correct);
      return;
    }

    if (correct === true) {
      this.getAnswerAndData(true);
      this.rightAnswer(1200);
    } else {
      this.getAnswerAndData(false);
      this.wrongAnswer();
    }
  }

  checkAnswer(): Promise<any> | void {
    if (this.rightAnswerDelay) return undefined;

    const testMode = this.optionsTrue('testMode');
    const questionAnswer = this.questions[this.questionNumber]!.answer;
    const userAnswer = this.answer;
    const intervalName = questionAnswer.intervalName === userAnswer.intervalName;
    const intervalQuality = questionAnswer.intervalQuality === userAnswer.intervalQuality;
    const correct = intervalName && intervalQuality;

    if (!userAnswer.intervalName) {
      return this.checkMark!.warn('You must provide a generic interval number.');
    }
    if (!userAnswer.intervalQuality) {
      return this.checkMark!.warn('You must give an interval quality first.');
    }

    if (testMode === true) {
      this.checkpointAnswer(correct);
      this.clearAnswer();
      return undefined;
    }

    if (correct === true) {
      this.getAnswerAndData(true);
      this.rightAnswer(1200);
      window.setTimeout(() => this.clearAnswer(), 1200);
    } else {
      this.getAnswerAndData(false);
      this.wrongAnswer();
    }
    return undefined;
  }

  clearAnswer(): void {
    this.answer = {
      intervalName: '',
      intervalQuality: '',
    };
    this.displayAnswer();
  }

  getAnswerAndData(correct?: boolean): AnswerAndData {
    const question = this.questions[this.questionNumber]!.question;

    const correctAnswer = this.questions[this.questionNumber]!.answer;
    const answerAndData = {
      question: `${question.note} to ${question.note2}`,
      questionType: 'IntervalsID',
      questionText: `${this.questionPrompt}`,
      questionSVG: this.notation!.exportSVG(),
      studentAnswerText: `${this.answer.intervalQuality}${this.answer.intervalName}`,
      correctAnswerText: `${correctAnswer.intervalQuality}${correctAnswer.intervalName}`,
      rightAnswers: correct ? 1 : 0,
      wrongAnswers: correct ? 0 : 1,
      skills: this.questions[this.questionNumber]!.skills,
    };

    return answerAndData;
  }

  gameOver(): void {
    this.instructionsDiv!.innerHTML = 'Nice work!';
    this.instructionsDiv!.setAttribute('role', 'alert');
    this.displayDiv.removeChild(this.lettersDiv!);
    super.gameOver();
  }

  playAnswer(): void {
    this.player!.play(this.currentQuestion.question.note.getMidiNumber(), {
      startTime: 0,
      stopTime: 0.6,
    });
    this.player!.play(this.currentQuestion.question.note2.getMidiNumber(), {
      startTime: 0.5,
      stopTime: 1.5,
    });
  }

  static registry: Record<string, any>[] = [
    {
      description: 'Identify Intervals',
      skills: [
        'ascending_major_perfect',
        'ascending_others',
        'descending_minor_perfect',
        'descending_others',
      ],
    },
    {
      description: 'Identify Half and Whole Steps',
      skills: ['chs', 'dhs', 'ws'],
      options: {
        halfWholeNames: true,
        decoyInterval: 'A2',
        halfWholeOnly: true,
      },
    },
  ];
}

export default IntervalsID;
